// src/components/notifications/BroadcastNotification.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { notificationService } from '../../services/notificationService';
import api from '../../services/api';

const BroadcastNotification = () => {
    usePageTitle('Send Notification');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Get current user info
    const [userType, setUserType] = useState('');
    const [userOrgId, setUserOrgId] = useState(null);
    const [userPremiseId, setUserPremiseId] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        type: 'info',
        target_type: 'all',
        target_org_id: null,
        target_premise_id: null,
        action_url: '',
        action_label: '',
        expires_at: ''
    });

    // Dropdown options
    const [organizations, setOrganizations] = useState([]);
    const [premises, setPremises] = useState([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [loadingPremises, setLoadingPremises] = useState(false);

    useEffect(() => {
        // Get user info from localStorage
        const storedUserData = localStorage.getItem('haazri_user');

        if (storedUserData) {
            const userData = JSON.parse(storedUserData);
            const storedUserType = userData.user_type;

            setUserType(storedUserType);
            setUserOrgId(userData.org_id);
            setUserPremiseId(userData.premises_id);

            // Set default target based on user type
            if (storedUserType === 'client_admin') {
                setFormData(prev => ({
                    ...prev,
                    target_type: 'organization',
                    target_org_id: userData.org_id
                }));
            } else if (storedUserType === 'app_admin') {
                setFormData(prev => ({
                    ...prev,
                    target_type: 'premise',
                    target_org_id: userData.org_id,
                    target_premise_id: userData.premises_id
                }));
            }
        }
    }, []);

    useEffect(() => {
        // Fetch organizations for core_admin
        if (userType === 'core_admin') {
            fetchOrganizations();
        }
        if (userType === 'client_admin' && userOrgId) {
            fetchPremises(userOrgId);
        }
    }, [userType]);

    useEffect(() => {
        // Fetch premises when organization is selected
        if (formData.target_type === 'premise' && formData.target_org_id) {
            fetchPremises(formData.target_org_id);
        }
    }, [formData.target_type, formData.target_org_id]);

    const fetchOrganizations = async () => {
        try {
            setLoadingOrganizations(true);
            const response = await api.get('/organizations/list');
            // API returns { success: true, data: [...] } where data is the array directly
            const orgs = response.data.data || response.data || [];
            setOrganizations(Array.isArray(orgs) ? orgs : []);
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
            showMessage('error', 'Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const fetchPremises = async (orgId) => {
        try {
            setLoadingPremises(true);
            const response = await api.get(`/premises?org_id=${orgId}`);
            // API returns { status, data: { data: { premises: [...] } } }
            const premisesData = response.data.data?.data?.premises ||
                response.data.data?.premises ||
                response.data.premises ||
                response.data.data ||
                response.data ||
                [];
            setPremises(Array.isArray(premisesData) ? premisesData : []);
        } catch (error) {
            console.error('Failed to fetch premises:', error);
            showMessage('error', 'Failed to load premises');
        } finally {
            setLoadingPremises(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTargetTypeChange = (e) => {
        const targetType = e.target.value;
        setFormData(prev => ({
            ...prev,
            target_type: targetType,
            target_org_id: (userType === 'client_admin' && (targetType === 'organization' || targetType === 'premise'))
                ? userOrgId
                : null,
            target_premise_id: targetType === 'premise' && userType === 'app_admin'
                ? userPremiseId
                : null
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            showMessage('error', 'Please enter a title');
            return;
        }

        if (!formData.message.trim()) {
            showMessage('error', 'Please enter a message');
            return;
        }

        if (formData.target_type === 'organization' && !formData.target_org_id) {
            showMessage('error', 'Please select an organization');
            return;
        }

        if (formData.target_type === 'premise' && !formData.target_premise_id) {
            showMessage('error', 'Please select a premise');
            return;
        }

        try {
            setLoading(true);

            // Prepare notification data
            const notificationData = {
                title: formData.title,
                message: formData.message,
                type: formData.type,
                target_type: formData.target_type
            };

            // Add optional fields
            if (formData.target_org_id) {
                notificationData.target_org_id = formData.target_org_id;
            }

            if (formData.target_premise_id) {
                notificationData.target_premise_id = formData.target_premise_id;
            }

            if (formData.action_url.trim()) {
                notificationData.action_url = formData.action_url;
            }

            if (formData.action_label.trim()) {
                notificationData.action_label = formData.action_label;
            }

            if (formData.expires_at) {
                notificationData.expires_at = formData.expires_at;
            }

            await notificationService.createNotification(notificationData);

            showMessage('success', 'Notification sent successfully!');

            // Reset form after 2 seconds
            setTimeout(() => {
                setFormData({
                    title: '',
                    message: '',
                    type: 'info',
                    target_type: userType === 'client_admin' ? 'organization' :
                        userType === 'app_admin' ? 'premise' : 'all',
                    target_org_id: userType === 'client_admin' ? userOrgId : null,
                    target_premise_id: userType === 'app_admin' ? userPremiseId : null,
                    action_url: '',
                    action_label: '',
                    expires_at: ''
                });
                navigate('/notifications');
            }, 2000);

        } catch (error) {
            console.error('Failed to send notification:', error);
            showMessage('error', error.response?.data?.message || 'Failed to send notification');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const getTargetDescription = () => {
        if (userType === 'client_admin') {
            return formData.target_type === 'premise'
                ? 'This notification will be sent to users in the selected premise in your organization'
                : 'This notification will be sent to all users in your organization';
        } else if (userType === 'app_admin') {
            return 'This notification will be sent to all users in your premise';
        }

        switch (formData.target_type) {
            case 'all':
                return 'This notification will be sent to all users in the system';
            case 'organization':
                return 'This notification will be sent to all users in the selected organization';
            case 'premise':
                return 'This notification will be sent to all users in the selected premise';
            default:
                return '';
        }
    };

    return (
        <div className="broadcast-notification">
            <div className="page-header">
                <div className="header-content">
                    <h1>
                        <i className="fas fa-bullhorn"></i>
                        Send Notification
                    </h1>
                    <p>Broadcast a notification to your users</p>
                </div>
                <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/notifications')}
                >
                    <i className="fas fa-arrow-left"></i>
                    Back to Notifications
                </button>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h2>Notification Details</h2>

                        <div className="form-group">
                            <label htmlFor="title">
                                Title <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter notification title"
                                required
                                maxLength="255"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">
                                Message <span className="required">*</span>
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder="Enter notification message"
                                rows="5"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="type">Notification Type</label>
                            <select
                                id="type"
                                name="type"
                                value={formData.type}
                                onChange={handleInputChange}
                            >
                                <option value="info">Info</option>
                                <option value="success">Success</option>
                                <option value="warning">Warning</option>
                                <option value="error">Error</option>
                                <option value="announcement">Announcement</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Target Audience</h2>

                        {(userType === 'core_admin' || userType === 'client_admin') ? (
                            <>
                                <div className="form-group">
                                    <label htmlFor="target_type">Send To</label>
                                    <select
                                        id="target_type"
                                        name="target_type"
                                        value={formData.target_type}
                                        onChange={handleTargetTypeChange}
                                    >
                                        {userType === 'core_admin' && <option value="all">All Users</option>}
                                        <option value="organization">{userType === 'core_admin' ? 'Specific Organization' : 'My Organization'}</option>
                                        <option value="premise">{userType === 'core_admin' ? 'Specific Premise' : 'Specific Premise (My Organization)'}</option>
                                    </select>
                                </div>

                                {formData.target_type === 'organization' && userType === 'core_admin' && (
                                    <div className="form-group">
                                        <label htmlFor="target_org_id">
                                            Select Organization <span className="required">*</span>
                                        </label>
                                        {loadingOrganizations ? (
                                            <div className="loading-spinner">Loading organizations...</div>
                                        ) : (
                                            <select
                                                id="target_org_id"
                                                name="target_org_id"
                                                value={formData.target_org_id || ''}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select an organization</option>
                                                {organizations.map(org => (
                                                    <option key={org.id} value={org.id}>
                                                        {org.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}

                                {formData.target_type === 'premise' && (
                                    <>
                                        {userType === 'core_admin' && (
                                            <div className="form-group">
                                                <label htmlFor="target_org_id">
                                                    Select Organization <span className="required">*</span>
                                                </label>
                                                {loadingOrganizations ? (
                                                    <div className="loading-spinner">Loading organizations...</div>
                                                ) : (
                                                    <select
                                                        id="target_org_id"
                                                        name="target_org_id"
                                                        value={formData.target_org_id || ''}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Select an organization</option>
                                                        {organizations.map(org => (
                                                            <option key={org.id} value={org.id}>
                                                                {org.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}

                                        {(userType === 'client_admin' || formData.target_org_id) && (
                                            <div className="form-group">
                                                <label htmlFor="target_premise_id">
                                                    Select Premise <span className="required">*</span>
                                                </label>
                                                {loadingPremises ? (
                                                    <div className="loading-spinner">Loading premises...</div>
                                                ) : (
                                                    <select
                                                        id="target_premise_id"
                                                        name="target_premise_id"
                                                        value={formData.target_premise_id || ''}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Select a premise</option>
                                                        {premises.map(premise => (
                                                            <option key={premise.id} value={premise.id}>
                                                                {premise.premises_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="info-box">
                                <i className="fas fa-info-circle"></i>
                                <p>{getTargetDescription()}</p>
                            </div>
                        )}

                        <div className="target-description">
                            <i className="fas fa-users"></i>
                            {getTargetDescription()}
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Optional Settings</h2>

                        <div className="form-group">
                            <label htmlFor="action_url">Action URL</label>
                            <input
                                type="text"
                                id="action_url"
                                name="action_url"
                                value={formData.action_url}
                                onChange={handleInputChange}
                                placeholder="/dashboard or https://example.com"
                                maxLength="500"
                            />
                            <small className="form-help">
                                URL to navigate when notification is clicked (e.g., /settings, /reports)
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="action_label">Action Label</label>
                            <input
                                type="text"
                                id="action_label"
                                name="action_label"
                                value={formData.action_label}
                                onChange={handleInputChange}
                                placeholder="View Details"
                                maxLength="100"
                            />
                            <small className="form-help">
                                Label to display for the action button
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="expires_at">Expiration Date</label>
                            <input
                                type="datetime-local"
                                id="expires_at"
                                name="expires_at"
                                value={formData.expires_at}
                                onChange={handleInputChange}
                            />
                            <small className="form-help">
                                When notification should no longer be shown (optional)
                            </small>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate('/notifications')}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane"></i>
                                    Send Notification
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            <style jsx>{`
                .broadcast-notification {
                    padding: 2rem;
                    max-width: 900px;
                    margin: 0 auto;
                }

                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .header-content h1 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .header-content h1 i {
                    color: #3b82f6;
                }

                .header-content p {
                    margin: 0;
                    color: #6b7280;
                }

                .message {
                    padding: 1rem 1.25rem;
                    border-radius: 10px;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 500;
                    animation: slideDown 0.3s ease-out;
                }

                .message.success {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }

                .message.error {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .form-container {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    padding: 2rem;
                }

                .form-section {
                    margin-bottom: 2rem;
                    padding-bottom: 2rem;
                    border-bottom: 2px solid #e5e7eb;
                }

                .form-section:last-of-type {
                    border-bottom: none;
                }

                .form-section h2 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 1.5rem 0;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .required {
                    color: #ef4444;
                }

                .form-group input[type="text"],
                .form-group input[type="datetime-local"],
                .form-group textarea,
                .form-group select {
                    width: 100%;
                    padding: 0.75rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }

                .form-group input:focus,
                .form-group textarea:focus,
                .form-group select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-group textarea {
                    resize: vertical;
                    font-family: inherit;
                }

                .form-help {
                    display: block;
                    margin-top: 0.5rem;
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .info-box {
                    background: #dbeafe;
                    border: 2px solid #93c5fd;
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .info-box i {
                    color: #3b82f6;
                    font-size: 1.25rem;
                }

                .info-box p {
                    margin: 0;
                    color: #1e40af;
                    font-weight: 500;
                }

                .target-description {
                    background: #f9fafb;
                    border-left: 4px solid #3b82f6;
                    padding: 1rem;
                    margin-top: 1rem;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #4b5563;
                }

                .target-description i {
                    color: #3b82f6;
                }

                .loading-spinner {
                    padding: 1rem;
                    text-align: center;
                    color: #6b7280;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    margin-top: 2rem;
                }

                .btn {
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    border: none;
                    font-size: 1rem;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
                }

                .btn-secondary {
                    background: white;
                    color: #374151;
                    border: 2px solid #e5e7eb;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                @media (max-width: 768px) {
                    .broadcast-notification {
                        padding: 1rem;
                    }

                    .page-header {
                        flex-direction: column;
                    }

                    .header-content h1 {
                        font-size: 1.5rem;
                    }

                    .form-container {
                        padding: 1.5rem;
                    }

                    .form-actions {
                        flex-direction: column-reverse;
                    }

                    .btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default BroadcastNotification;
